const TABLE_SELECTOR = '[data-scale-compare-table]';
const MOBILE_QUERY = window.matchMedia('(max-width: 1023px)');
const instances = new Map();

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function readLabels(table) {
  return Array.from(table.querySelectorAll('[data-scale-compare-label]')).map((node) => {
    const fromAttr = node.getAttribute('data-label-text');
    if (fromAttr != null && fromAttr !== '') return fromAttr;
    return node.textContent?.trim() || '';
  });
}

function readProducts(table) {
  return Array.from(table.querySelectorAll('[data-column-role="product"]')).map((column) => {
    const values = Array.from(column.querySelectorAll('[data-scale-compare-value]')).map((node) => {
      const fromAttr = node.getAttribute('data-value-text');
      if (fromAttr != null && fromAttr !== '') return fromAttr;
      return node.textContent?.trim() || '—';
    });

    return {
      column,
      tabName: column.getAttribute('data-tab-name') || column.getAttribute('data-product-name') || 'Product',
      highlight: column.getAttribute('data-highlight') === 'true',
      headerHtml: column.querySelector('[data-scale-compare-product-header]')?.innerHTML || '',
      values,
    };
  });
}

class ScaleCompareMobile {
  constructor(table) {
    this.table = table;
    this.root = table.querySelector('[data-scale-compare-mobile]');
    this.activeIndex = 0;
    this.handlers = [];
    this.built = false;
  }

  build() {
    if (!this.root) return;

    const labels = readLabels(this.table);
    const products = readProducts(this.table);
    if (products.length === 0) {
      this.root.hidden = true;
      this.root.innerHTML = '';
      this.built = false;
      return;
    }

    const highlightIndex = products.findIndex((product) => product.highlight);
    this.activeIndex = highlightIndex >= 0 ? highlightIndex : 0;

    const tabs = products
      .map((product, index) => {
        const selected = index === this.activeIndex;
        return `
          <button
            type="button"
            class="sb-scale-compare-mobile__tab"
            role="tab"
            id="ScaleCompareTab-${this.table.id || 'table'}-${index}"
            aria-controls="ScaleComparePanel-${this.table.id || 'table'}-${index}"
            aria-selected="${selected ? 'true' : 'false'}"
            tabindex="${selected ? '0' : '-1'}"
            data-scale-compare-tab="${index}"
          >${escapeHtml(product.tabName)}</button>
        `;
      })
      .join('');

    const panels = products
      .map((product, index) => {
        const selected = index === this.activeIndex;
        const rows =
          labels.length > 0
            ? labels
                .map((label, rowIndex) => {
                  const value = product.values[rowIndex] ?? '—';
                  return `
                    <div class="sb-scale-compare-mobile__row">
                      ${label ? `<span class="sb-scale-compare-mobile__label">${escapeHtml(label)}</span>` : ''}
                      <span class="sb-scale-compare-mobile__value">${escapeHtml(value)}</span>
                    </div>
                  `;
                })
                .join('')
            : product.values
                .map(
                  (value) => `
                    <div class="sb-scale-compare-mobile__row">
                      <span class="sb-scale-compare-mobile__value">${escapeHtml(value)}</span>
                    </div>
                  `
                )
                .join('');

        return `
          <article
            class="sb-scale-compare-mobile__panel${product.highlight ? ' is-highlight' : ''}"
            role="tabpanel"
            id="ScaleComparePanel-${this.table.id || 'table'}-${index}"
            aria-labelledby="ScaleCompareTab-${this.table.id || 'table'}-${index}"
            data-scale-compare-panel="${index}"
            data-highlight="${product.highlight ? 'true' : 'false'}"
            ${selected ? '' : 'hidden'}
          >
            <div class="sb-scale-compare-mobile__header">${product.headerHtml}</div>
            <div class="sb-scale-compare-mobile__rows">${rows}</div>
          </article>
        `;
      })
      .join('');

    this.root.innerHTML = `
      <div class="sb-scale-compare-mobile__tabs" role="tablist" aria-label="Compare products">${tabs}</div>
      <div class="sb-scale-compare-mobile__panels">${panels}</div>
    `;
    this.root.hidden = false;
    this.built = true;
    this.bind();
    this.setActive(this.activeIndex, true);
  }

  bind() {
    this.unbind();
    const tabs = Array.from(this.root.querySelectorAll('[data-scale-compare-tab]'));
    tabs.forEach((tab) => {
      const clickHandler = () => this.setActive(Number.parseInt(tab.dataset.scaleCompareTab, 10));
      const keyHandler = (event) => this.onKeydown(event, tabs);
      tab.addEventListener('click', clickHandler);
      tab.addEventListener('keydown', keyHandler);
      this.handlers.push([tab, clickHandler, keyHandler]);
    });
  }

  unbind() {
    this.handlers.forEach(([tab, clickHandler, keyHandler]) => {
      tab.removeEventListener('click', clickHandler);
      tab.removeEventListener('keydown', keyHandler);
    });
    this.handlers = [];
  }

  onKeydown(event, tabs) {
    const current = Number.parseInt(event.currentTarget.dataset.scaleCompareTab, 10);
    if (!Number.isFinite(current) || tabs.length === 0) return;

    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.setActive(next);
    tabs[next]?.focus();
  }

  setActive(index, silent = false) {
    const tabs = Array.from(this.root.querySelectorAll('[data-scale-compare-tab]'));
    const panels = Array.from(this.root.querySelectorAll('[data-scale-compare-panel]'));
    if (tabs.length === 0) return;

    const nextIndex = Math.min(tabs.length - 1, Math.max(0, index));
    if (!silent && nextIndex === this.activeIndex) return;
    this.activeIndex = nextIndex;
    this.root.dataset.scaleCompareMobileActive = String(nextIndex);

    tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === nextIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== nextIndex;
    });
  }

  destroy() {
    this.unbind();
    if (this.root) {
      this.root.innerHTML = '';
      this.root.hidden = true;
    }
    this.built = false;
  }
}

function ensureInstance(table) {
  if (!instances.has(table)) {
    instances.set(table, new ScaleCompareMobile(table));
  }
  return instances.get(table);
}

function syncMobile(root = document) {
  const tables = root.matches?.(TABLE_SELECTOR) ? [root] : root.querySelectorAll?.(TABLE_SELECTOR) || [];

  tables.forEach((table) => {
    const instance = ensureInstance(table);
    if (MOBILE_QUERY.matches) {
      instance.build();
    } else {
      instance.destroy();
    }
  });
}

function destroyMobile(root = document) {
  instances.forEach((instance, table) => {
    if (root !== document && !root.contains(table)) return;
    instance.destroy();
    instances.delete(table);
  });
}

MOBILE_QUERY.addEventListener?.('change', () => syncMobile());
document.addEventListener('shopify:section:load', (event) => syncMobile(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyMobile(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncMobile(), { once: true });
} else {
  syncMobile();
}
